"use client";

import "@membership/ui.components/style.css";
import { PromoCarousel } from "@membership/ui.components";
import { Logger } from "lib/logger";
import { usePromotionalBanner } from "packages/membership/components/PromotionalBanner/usePromotionalBanner";
import { withErrorBoundary } from "react-error-boundary";
import { bannerWrapper, animations } from "./PromotionalBanner.css";
import { useEffect } from "react";
import { useExperimentViewed } from "tracking/hooks/useExperimentViewed";
import { useTracking } from "./useTracking";
import type { ExperimentDetails } from "./types";
import type { ColourVariant } from "@shared/ui.design-system";
import type { IPromotionalBannerResponse } from "packages/membership/components/PromotionalBanner/types";

type PropType = {
  experiment: ExperimentDetails;
  backgroundColourSsr: string;
  fontColourVariantSsr: ColourVariant;
  promotionalBanner: IPromotionalBannerResponse;
};

export function PromotionalBannerBase({
  experiment,
  backgroundColourSsr,
  fontColourVariantSsr,
  promotionalBanner
}: PropType) {
  const tracking = useTracking();

  const trackExperimentViewed = useExperimentViewed();
  useEffect(() => {
    trackExperimentViewed(experiment.id, Number(experiment.value));
  }, [experiment.id, experiment.value, trackExperimentViewed]);

  const { loading, promotionalBannerDetails } = promotionalBanner;

  if (!loading) {
    const contentMessages = promotionalBannerDetails?.contentMessages || [];
    if (!contentMessages?.length) {
      return (
        <div
          className={`${bannerWrapper} ${animations.hide}`}
          style={{ background: backgroundColourSsr }}
        />
      );
    }

    return (
      <div
        className={bannerWrapper}
        style={{ background: backgroundColourSsr }}
        data-testid="promotional-banner"
      >
        <PromoCarousel
          slides={contentMessages}
          fontColour={fontColourVariantSsr}
          tracking={tracking}
        />
      </div>
    );
  }

  return (
    <div
      className={bannerWrapper}
      style={{ background: backgroundColourSsr }}
    />
  );
}

function PromotionalBannerClient(props: Omit<PropType, "promotionalBanner">) {
  const promotionalBanner = usePromotionalBanner();

  return (
    <PromotionalBannerBase {...props} promotionalBanner={promotionalBanner} />
  );
}

export const PromotionalBanner = withErrorBoundary(PromotionalBannerClient, {
  fallback: null,
  onError(error) {
    Logger.warn(`PromotionalBanner client component failed to load. ${error}`);
  }
});
